import {
  Pagination,
  PaginationContainer,
  PaginationNext,
  PaginationPage,
  PaginationPageGroup,
  PaginationPrevious,
  PaginationSeparator,
  usePagination,
} from "@ajna/pagination";
import {
  Center,
  Flex,
  HStack,
  Icon,
  IconButton,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { CaretDoubleLeft, CaretDoubleRight, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { showErrorToast } from "../../utils";

// Modern paginator used across the app. Rounded pill buttons, lime-accented
// current page, subtle outlines, Phosphor chevron icons. The external API
// (selectedPage, numberOfPages, handlePageSelection) is unchanged so every
// existing caller picks up the new look without touching their code.
//
// Optional controls — when pageSize is passed, the bar also renders
// Page X of Y, Go to page, and a Show N selector (matching the older
// DataTable paginator).
function Paginator({
  selectedPage,
  numberOfPages,
  handlePageSelection,
  pageSize,
  pageSizeOptions = [10, 20, 30, 40, 50],
  onPageSizeChange,
  totalCount,
}) {
  const { t } = useTranslation("translation", { keyPrefix: "dataTable" });
  const toast = useToast();
  const nonIntegerSymbols = ["e", "E", ",", ".", "-", "+"];

  const {
    setCurrentPage,
    pagesCount,
    pages,
  } = usePagination({
    pagesCount: numberOfPages,
    limits: {
      outer: 1,
      inner: 1,
    },
    initialState: { currentPage: 1 },
  });

  const handlePageChange = (nextPage) => {
    setCurrentPage(nextPage);
    handlePageSelection(nextPage);
  };

  const pageStyles = {
    w: "28px",
    h: "28px",
    minW: "28px",
    fontSize: "12px",
    fontWeight: "500",
    color: "gray.600",
    bg: "transparent",
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "full",
    margin: "0 3px",
    _hover: { bg: "gray.50", borderColor: "gray.300" },
    _focus: { boxShadow: "none" },
    _current: {
      bg: "lime",
      color: "pureBlack",
      borderColor: "lime",
      fontWeight: "600",
    },
  };

  const navStyles = {
    w: "28px",
    h: "28px",
    minW: "28px",
    p: 0,
    bg: "transparent",
    color: "gray.600",
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "full",
    _hover: { bg: "gray.50", borderColor: "gray.300" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
    _focus: { boxShadow: "none" },
  };

  const atFirstPage = selectedPage <= 1;
  const atLastPage = selectedPage >= pagesCount;

  const showExtraControls = pageSize != null;
  const minPageSize = pageSizeOptions.length > 0 ? Math.min(...pageSizeOptions) : 10;
  // We hide the whole bar only when there's no point in any of it: a single
  // page AND the underlying list is small enough that even the smallest page
  // size couldn't paginate it. Otherwise we keep the "Show N rows" selector
  // visible so the user can re-paginate without first re-expanding the list.
  const hideEverything = numberOfPages <= 1 && (
    totalCount == null ? !showExtraControls : totalCount < minPageSize
  );
  if (hideEverything) {
    return null;
  }
  const showNav = numberOfPages > 1;

  const navBar = (
    <Pagination
      pagesCount={pagesCount}
      currentPage={selectedPage}
      onPageChange={handlePageChange}
    >
      <PaginationContainer align="center">
        <IconButton
          aria-label="First page"
          icon={<Icon as={CaretDoubleLeft} boxSize="12px" />}
          onClick={() => !atFirstPage && handlePageChange(1)}
          isDisabled={atFirstPage}
          {...navStyles}
          mr={1}
        />
        <PaginationPrevious {...navStyles} mr={2}>
          <Icon as={CaretLeft} boxSize="12px" />
        </PaginationPrevious>
        <PaginationPageGroup
          separator={
            <PaginationSeparator
              jumpSize={10}
              w="28px"
              h="28px"
              minW="28px"
              fontSize="12px"
              color="gray.400"
              bg="transparent"
              borderRadius="full"
              margin="0 3px"
            />
          }
        >
          {pages.map((page) => (
            <PaginationPage
              key={`pagination_page_${page}`}
              page={page}
              {...pageStyles}
            />
          ))}
        </PaginationPageGroup>
        <PaginationNext {...navStyles} ml={2}>
          <Icon as={CaretRight} boxSize="12px" />
        </PaginationNext>
        <IconButton
          aria-label="Last page"
          icon={<Icon as={CaretDoubleRight} boxSize="12px" />}
          onClick={() => !atLastPage && handlePageChange(pagesCount)}
          isDisabled={atLastPage}
          {...navStyles}
          ml={1}
        />
      </PaginationContainer>
    </Pagination>
  );

  if (!showExtraControls) {
    return showNav ? <Center>{navBar}</Center> : null;
  }

  return (
    <Flex
      width="100%"
      align="center"
      justify="space-between"
      gap={6}
      flexWrap="wrap"
      fontSize="13px"
      color="gray.600"
    >
      <HStack spacing={6} flexWrap="wrap">
        {showNav && pages.length < pagesCount && (
        <HStack spacing={2}>
          <Text fontSize="13px" whiteSpace="nowrap">{t("page")}</Text>
          <NumberInput
            w="56px"
            size="sm"
            min={1}
            max={pagesCount}
            clampValueOnBlur={false}
            value={selectedPage}
            onKeyDown={(event) => nonIntegerSymbols.includes(event.key) && event.preventDefault()}
            onChange={(value) => {
              if (value === "") return;
              const num = Number(value);
              if (!Number.isFinite(num) || num < 1 || num > pagesCount) {
                showErrorToast(toast, t("invalidPageMessage"));
                return;
              }
              toast.closeAll();
              handlePageChange(num);
            }}
            aria-label={t("goToPage")}
          >
            <NumberInputField
              fontSize="13px"
              h="28px"
              minH="28px"
              borderRadius="full"
              border="1px solid"
              borderColor="gray.200"
              px={2}
              textAlign="center"
              title={t("goToPage")}
              _hover={{ borderColor: "gray.300" }}
              _focus={{ borderColor: "lime", boxShadow: "none" }}
            />
          </NumberInput>
          <Text fontSize="13px" whiteSpace="nowrap">
            {`${t("of")} `}
            <Text as="span" fontWeight="bold">
              {pagesCount}
            </Text>
          </Text>
        </HStack>
        )}
        {pageSize != null && onPageSizeChange && (
          <HStack spacing={2}>
            <Text fontSize="13px" whiteSpace="nowrap">{`${t("show")}:`}</Text>
            <Select
              w="120px"
              size="sm"
              fontSize="13px"
              h="28px"
              borderRadius="full"
              borderColor="gray.200"
              color="gray.600"
              sx={{ paddingInlineStart: "14px", paddingInlineEnd: "26px" }}
              _hover={{ borderColor: "gray.300" }}
              _focusVisible={{ borderColor: "lime", boxShadow: "none" }}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              title={`${t("show")} ${pageSize}`}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {`${opt} rows`}
                </option>
              ))}
            </Select>
          </HStack>
        )}
      </HStack>
      {showNav && navBar}
    </Flex>
  );
}

export default Paginator;
